/*

This provides some functions to communicate with reservatech

Requires:
  JQuery
  The variable Relativerootpath set correctly

*/

// Plugin name:
const { defaults } = require('lodash');
const jscookie = require('js-cookie');
const base64url = require('base64-url');
// Public Functions ----------------------------------------

/* -------------------------------------------------------
Ticketing system API
--------------------------------------------------------*/

const TS_HEADER_AUTHTOKEN_NAME = 'rese577';

type MVCAPIAJAXSettings = JQuery.AjaxSettings<any> | {
  url?: string,
  type?: string,
  contentType?: string,
  data?: string,
  headers?: any,
  error?: (jqXHR: any, textStatus: string, errorThrown: string) => void,
  success?: (data: any, status: string, jqXHR: any) => void,
};

// A TS Service Call object represent a running call on which
// you can add handler for all the
export class TSServiceCall {
  private promise: Promise<any>;
  private ajaxCall: JQuery.jqXHR<any>;
  private tsAPIService: TSAPIService;
  private jquery: JQueryStatic;
  private fullCtlURL: string;
  private postData: any;
  private ajaxSettings: JQuery.AjaxSettings<any>;
  private afterCallback: () => void;
  private communicationErrorCallback: () => void;
  private successCallback: (data: any) => void;
  private notSuccessCallback: (data: any) => void;
  private notLoggedInCallback: () => void;
  private accessDeniedCallback: () => void;
  private wasAborted: boolean;
  private wholeResourceURL: string;
  private verb: string;
  public resultAuthTokenStamp: string;
  private settings: any;

  /**
   * failedHandledByPromise means that a promise has been connected and that we don't want the default behaviour to happen.
   */
  private failedHandledByPromise: boolean;

  constructor(tsAPIService: TSAPIService, jquery: JQueryStatic, fullCtlURL: string, postData: any, ajaxSettings: JQuery.AjaxSettings<any>) {
    this.tsAPIService = tsAPIService;
    this.jquery = jquery;
    this.fullCtlURL = fullCtlURL;
    this.postData = postData;
    this.ajaxSettings = defaults(ajaxSettings, {}); // No default yet!
    
    const getAfterCb = () => {
      if (this.afterCallback != null) { return this.afterCallback; } else { return this.tsAPIService.afterCallbackDefault; }
    };

    const headers = {};
    let tokenFromCookie = false;
    let cookieAuth = null;
    if(this.tsAPIService.authTokenStamp) { // Priority on the manual setting
      headers[TS_HEADER_AUTHTOKEN_NAME] = this.tsAPIService.authTokenStamp;
    } else if(this.tsAPIService.readAuthFromCookie) {
      tokenFromCookie = true;
      cookieAuth = jscookie.get(this.tsAPIService.authTokenCookieName);
      if(cookieAuth) {
        headers[TS_HEADER_AUTHTOKEN_NAME] = cookieAuth;
      }
    }

    // Prepare the ajax call
    this.ajaxCall = $.ajax(Object.assign({}, this.ajaxSettings, {
      url: this.fullCtlURL,
      type: 'POST',
      contentType: 'application/json; charset=utf-8', // Always JSON
      data: JSON.stringify(this.postData),
      headers: headers
    }));

    // Prepare a promise that preprocess the result
    this.promise = new Promise((resolve, reject) => {

      // Success
      this.ajaxCall.done((data, _textStatus, jqXHR) => {
        let cb;
        const authHeader = jqXHR.getResponseHeader(TS_HEADER_AUTHTOKEN_NAME);
        if (authHeader && tokenFromCookie && this.tsAPIService.saveAuthInCookie) { // If we received a new authtoken by the header AND we already have an authtoken cookie, let's keep it updated!
  
          jscookie.set(this.tsAPIService.authTokenCookieName, authHeader, { 
            path: '/', 
            secure: tsAPIService.secureCookie, sameSite: tsAPIService.sameSiteCookie 
          });
        }
  
        if ((data.message == null)) {
          data.message = data.Message; // Do this to keep a certain standard. If there's no message then nothing happens!
        }
  
        // this.failedHandledByPromise means that a promise has been connected and that we don't want the default behaviour to happen.
        if ((data.IsSuccess === true) || (data.isSuccess === true)) {
          cb = this.successCallback;
          if(!cb && !this.failedHandledByPromise) cb = this.tsAPIService.successCallbackDefault;
          resolve(data)
        } else {
          cb = this.notSuccessCallback;
          if(!cb && !this.failedHandledByPromise) cb = this.tsAPIService.notSuccessCallbackDefault;
          reject(data)
        }
        if(cb) cb(data);
        
        getAfterCb()();
      });

      // Failed
      return this.ajaxCall.fail((jqXHR, textStatus, err) => {
        let cb = null;
        // this.failedHandledByPromise means that a promise has been connected and that we don't want the default behaviour to happen.
        switch (jqXHR.status) {
          case 401:
            cb = this.notLoggedInCallback;
            if(!cb && !this.failedHandledByPromise) cb = this.tsAPIService.notLoggedInCallbackDefault;
            break;
          case 403:
            cb = this.accessDeniedCallback;
            if(!cb && !this.failedHandledByPromise) cb = this.tsAPIService.accessDeniedCallbackDefault;
            break;
          default:
            cb = this.communicationErrorCallback;
            if(!cb && !this.failedHandledByPromise) cb = this.tsAPIService.communicationErrorCallbackDefault;
        }
  
        if(cb) cb();

        reject(err);
        getAfterCb()();
      });
    });

  }

  /** @deprecated Use this a promise instead */
  commError(communicationErrorCallback) { this.communicationErrorCallback = communicationErrorCallback; return this; } // If the HTTP call failed
  /** @deprecated Use this a promise instead */
  success(successCallback) { this.successCallback = successCallback; return this; } // If the response is success!
  /** @deprecated Use this a promise instead */
  notSuccess(notSuccessCallback) { 
    this.promise.then(null , () => { /* The caller use a legacy way without promise. We then prevent the un handled promise from failing */}); 
    this.notSuccessCallback = notSuccessCallback; return this; 
  } // If the response is not success
  /** @deprecated Use this a promise instead. We should probably add some kind of decorators to trap this. */
  notLoggedIn(notLoggedInCallback) { this.notLoggedInCallback = notLoggedInCallback; return this; } // Use this if you want to override the way it'll react to an unauthorized request. Else, default rules is to return in login page.
  /** @deprecated Use this a promise instead. We should probably add some kind of decorators to trap this. */
  accessDenied(accessDeniedCallback) { this.accessDeniedCallback = accessDeniedCallback; return this; }
  /** @deprecated Use this a promise instead */
  after(afterCallback) { this.afterCallback = afterCallback; return this; } // Always called after everything.

  abort() {
    return this.ajaxCall.abort();
  }

  // Thenable Object! ***** (Promise A+, https://promisesaplus.com/)
  then(onFulfilled: (value: any) => void, onRejected?: (err: any) => void) {
    this.failedHandledByPromise = !!onRejected; // Compatility with the old way of doing things. If the promise is used, bypass the default fail callback. (Won't show a Alert on screen)
    return this.promise.then(onFulfilled, onRejected);
  }
  catch(cb: (err: any) => void) { return this.promise.catch(cb); }
  finally(cb: () => void) { return this.promise.finally(cb); }

}

// The Ticketing System API
export class TSAPIService {
  public ctlBaseURL: string;
  public authTokenStamp: string;
  public communicationErrorCallbackDefault: () => void;
  public successCallbackDefault: () => void;
  public notSuccessCallbackDefault: (data: any) => void;
  public notLoggedInCallbackDefault: () => void;
  public accessDeniedCallbackDefault: () => void;
  public afterCallbackDefault: () => void;
  public readAuthFromCookie: boolean;
  public saveAuthInCookie: boolean;
  public authTokenCookieName: string;
  public secureCookie: boolean;
  public sameSiteCookie: string;

  constructor(ctlBaseURL, authTokenStamp) {
    // Default reactions
    this.postCtl = this.postCtl.bind(this);
    this.ctlBaseURL = ctlBaseURL;
    this.authTokenStamp = authTokenStamp;
    this.communicationErrorCallbackDefault = () => {
      return alert("Erreur de communication avec le serveur, veuillez réessayer.");
    };

    this.notSuccessCallbackDefault = data => {
      return alert("Une erreur est survenue lors du traitement.");
    };

    this.notLoggedInCallbackDefault = () => {
      return alert("Vous devez être connecté."); // TODO : Bring translation here
    };

    this.accessDeniedCallbackDefault = () => {
      return alert("Accès non permis."); // TODO : Bring translation here
    };

    this.successCallbackDefault = () => {};

    this.afterCallbackDefault = () => {};

    this.readAuthFromCookie = false; // true: take the authstamp cookie and pass it in the call headers  (You must set @authTokenCookieName)
    this.saveAuthInCookie = false; // true: take the authstamp from the response header and write it in the cookie (You must set @authTokenCookieName)
    this.authTokenCookieName = null; // Set this if you want to connect a cookie with the headers
    this.secureCookie = false;
    this.sameSiteCookie = undefined;
  }
    
  postCtl(ctlURL: string, postData?: any, ajaxSettings?: MVCAPIAJAXSettings) {
    return new TSServiceCall(this, $, `${this.ctlBaseURL}/${ctlURL}`, postData, ajaxSettings);
  }

  commError(communicationErrorCallbackDefault) { this.communicationErrorCallbackDefault = communicationErrorCallbackDefault; return this; } // If the HTTP call failed
  success(successCallbackDefault) { this.successCallbackDefault = successCallbackDefault; return this; } // If the response is success!
  notSuccess(notSuccessCallbackDefault) { this.notSuccessCallbackDefault = notSuccessCallbackDefault; return this; } // If the response is not success
  notLoggedIn(notLoggedInCallbackDefault) { this.notLoggedInCallbackDefault = notLoggedInCallbackDefault; return this; } // Use this if you want to override the way it'll react to an unauthorized request. Else, default rules is to return in login page.
  accessDenied(accessDeniedCallbackDefault) { this.accessDeniedCallbackDefault = accessDeniedCallbackDefault; return this; }
  after(afterCallbackDefault) { this.afterCallbackDefault = afterCallbackDefault; return this; } // Always called after everything.
}




/* ********************************************************
  Login
**********************************************************/
export let loginCtl: { 
  logout: (ts: TSAPIService) => TSServiceCall, 
  logSellerOut: (ts: TSAPIService) => TSServiceCall, 
} = {
  logout: ts => ts.postCtl("login/logout", {}),
  logSellerOut: ts => ts.postCtl("login/logSellerOut", {})
};
